import { HttpEmployerService } from './../../../../service/http-employer.service';
import { InsurancePremiumRecipientDto, ProductExtendedDetailDto, ProductExtendedDto, ProductOfferBenefitSplitDto, ProductOfferDto, ProductOfferLineDetailDto, ProductOfferLineDto, ProductRuleDto, ProductBankAccountDto, ProductOfferBenefitRuleDto, ProductTypeEnum } from './../../../../service/dto-insurance';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { HttpInsuranceProductService } from '../../../../service/http-insurance-product.service';
import { BenefitTypeEnum, ProductDto } from '../../../../service/dto-insurance';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ProductEditRuleModalComponent } from './components/product-edit-rule-modal/product-edit-rule-modal.component';
import { ProductEditOfferModalComponent } from './components/product-edit-offer-modal/product-edit-offer-modal.component';
import { ProductEditExtendedModalComponent } from './components/product-edit-extended-modal/product-edit-extended-modal.component';
import { EmployerDto, WorkflowActionTypeEnum, WorkflowStepButtonActionDto, WorkflowStepButtonDto } from '../../../../service/dto';
import { ProductEditBankAccountModalComponent } from './components/product-edit-bank-account-modal/product-edit-bank-account-modal.component';
import { NzUploadFile, NzUploadXHRArgs } from 'ng-zorro-antd/upload';
import { HttpFileService } from '../../../../service/http-file.service';
import { HttpWorkflowService } from '../../../../service/http-workflow.service';

@Component({
	selector: 'app-product-edit',
	standalone: false,
	templateUrl: './product-edit.component.html',
	styleUrl: './product-edit.component.scss'
})
export class ProductEditComponent implements OnInit, OnDestroy {
	product?: ProductDto;
	form!: FormGroup;
	subscription = new Subscription();
	selectedTab = 1;

	availableProductTypes: ProductTypeEnum[] = ['Funeral', 'TemporaryDisability', 'PermanentDisability', 'CreditLife', 'GroupLife', 'PermanentHealthInsurance'];
	availableBenefitTypes: BenefitTypeEnum[] = ['LumpSum', 'Monthly'];
	availablePremiumRecipients: InsurancePremiumRecipientDto[] = [];
	availableUnderwriters: InsurancePremiumRecipientDto[] = [];
	availablePolicyGroups: InsurancePremiumRecipientDto[] = [];
	availableEmployers: EmployerDto[] = [];

	emailPlaceholders: any;
	paymentSentAttachmentsNew: NzUploadFile[] = [];


	compareById = (o1: any, o2: any): boolean => o1?.id === o2?.id;

	constructor(
		private fb: FormBuilder,
		private activatedRoute: ActivatedRoute,
		private router: Router,
		private modalService: NzModalService,
		private httpService: HttpInsuranceProductService,
		private fileService: HttpFileService,
		private httpEmployerService: HttpEmployerService,
		private workflowService: HttpWorkflowService) { }

	ngOnInit(): void {
		forkJoin({
			premiumRecipients: this.httpService.getPremiumRecipients(),
			employers: this.httpEmployerService.getAll()
		}).subscribe(({ premiumRecipients, employers }) => {
			this.availablePremiumRecipients = premiumRecipients;
			this.availableUnderwriters = premiumRecipients.filter(item => item.type === 'InsuranceUnderwriter');
			this.availablePolicyGroups = premiumRecipients.filter(item => item.type === 'InsurancePolicyGroup');
			this.availableEmployers = employers;
		});

		this.subscription.add(this.activatedRoute.paramMap.subscribe(paramMap => {
			if (paramMap.get('id')) {
				this.httpService.get(+paramMap.get('id')!).subscribe(result => {
					this.product = result;
					this.workflowService.getEmailPlaceholders(this.product!.workflow!.id, this.product!.workflow!)
						.subscribe(r => this.emailPlaceholders = r);

					this.form = this.fb.group({
						id: [this.product.id],
						name: [this.product.name, [Validators.required]],
						description: [this.product.description],
						productType: [this.product.productType, Validators.required],
						benefitType: [this.product.benefitType, Validators.required],
						lapsedPremiumWindowInMonths: [this.product.lapsedPremiumWindowInMonths, Validators.required],
						maxAllowedLapsedPremiumsInWindowPeriod: [this.product.maxAllowedLapsedPremiumsInWindowPeriod, Validators.required],
						maxAllowedConsecutiveLapsedPremiums: [this.product.maxAllowedConsecutiveLapsedPremiums, Validators.required],
						primaryPremiumRecipient: [this.product.primaryPremiumRecipient, Validators.required],
						premiumSplitPaymentDayOfMonth: [this.product.premiumSplitPaymentDayOfMonth, Validators.required],
						raisePremiumDayOfMonth: [this.product.raisePremiumDayOfMonth, Validators.required],
						policyGroup: [this.product.policyGroup],
						employer: [this.product.employer],
						policyNumberPrefix:[this.product.policyNumberPrefix],
						enableBenefitPaymentFileGeneration: [this.product.enableBenefitPaymentFileGeneration],
						reviewMonth: [this.product.reviewMonth],
						rules: this.fb.array(this.product.rules.map(item => this.buildRuleGroup(item))),
						offers: this.fb.array((this.product.offers ?? []).map(item => this.buildOfferGroup(this.product!.productType, item))),
						extendeds: this.fb.array((this.product.extendeds ?? []).map(item => this.buildExtendedGroup(item))),
						bankAccounts: this.fb.array((this.product.bankAccounts ?? []).map(item => this.buildProductBankAccount(item))),
						paymentSentCommunication: this.fb.group({
							name: [this.product.paymentSentCommunication?.name ?? ''],
							emailFrom: [this.product.paymentSentCommunication?.emailFrom ?? ''],
							emailTo: [this.product.paymentSentCommunication?.emailTo ?? ''],
							emailCc: [this.product.paymentSentCommunication?.emailCc ?? ''],
							emailBcc: [this.product.paymentSentCommunication?.emailBcc ?? ''],
							emailSubject: [this.product.paymentSentCommunication?.emailSubject ?? ''],
							emailBody: [this.product.paymentSentCommunication?.emailBody ?? ''],
							attachments: this.fb.array(this.product.paymentSentCommunication?.attachments ?? [])
						})
					});
				});

			} else {
				this.form = this.fb.group({
					id: [],
					name: ['', [Validators.required]],
					description: [''],
					productType: ['', Validators.required],
					benefitType: ['', Validators.required],
					lapsedPremiumWindowInMonths: ['', Validators.required],
					maxAllowedLapsedPremiumsInWindowPeriod: ['', Validators.required],
					maxAllowedConsecutiveLapsedPremiums: ['', Validators.required],
					primaryPremiumRecipient: ['', Validators.required],
					premiumSplitPaymentDayOfMonth: [1, Validators.required],
					raisePremiumDayOfMonth: [1, Validators.required],
					policyNumberPrefix:[''],
					policyGroup: [undefined],
					employer: [undefined],
					enableBenefitPaymentFileGeneration: [true],
					reviewMonth: [1],
					rules: this.fb.array([]),
					offers: this.fb.array([]),
					extendeds: this.fb.array([]),
					bankAccounts: this.fb.array([])
				});
			}
		}));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
	getActionRowSpan(button: WorkflowStepButtonDto) {
		if (button.actions.length == 0) {
			return 1;
		}
		return button.actions.length;
	}
	// ─── Form Group Builders ──────────────────────────────────────────────────

	private buildRuleGroup(item?: ProductRuleDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			ruleType: [item?.ruleType ?? '', Validators.required],
			appliedTo: [item?.appliedTo ?? '', Validators.required],
			threshold: [item?.threshold ?? 0, Validators.required],
		});
	}

	private buildCommissionGroup(item?: any): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			recipient: [item?.recipient ?? null, Validators.required],
			commissionType: [item?.commissionType ?? 'Fixed', Validators.required],
			commissionAmount: [item?.commissionAmount ?? 0, Validators.required],
		});
	}

	private buildOfferLineDetailGroup(productType: ProductTypeEnum, item?: ProductOfferLineDetailDto): FormGroup {
		let group = this.fb.group({
			id: [item?.id ?? ''],
			fromDate: [item?.fromDate ? new Date(item.fromDate) : null, Validators.required],
			toDate: [item?.toDate ? new Date(item.toDate) : null],
			premiumType: [item?.premiumType ?? 'Fixed', Validators.required],
			premiumAmount: [item?.premiumAmount ?? 0, Validators.required],
			benefitType: [item?.benefitType ?? 'Fixed', Validators.required],
			appliesOnlyToNewPolicies: [item?.appliesOnlyToNewPolicies ?? false],
			commissions: this.fb.array((item?.commissions ?? []).map((c: any) => this.buildCommissionGroup(c))),
		}) as FormGroup;

		if (productType == 'Funeral') {
			group.addControl('benefitAmountNatural', this.fb.control(item?.benefitAmountNatural ?? item?.benefitAmount, Validators.required));
			group.addControl('benefitAmountAccidental', this.fb.control(item?.benefitAmountAccidental ?? item?.benefitAmount, Validators.required));
			group.addControl('benefitAmountSuicide', this.fb.control(item?.benefitAmountSuicide ?? item?.benefitAmount, Validators.required));
		} else {
			group.addControl('benefitAmount', this.fb.control(item?.benefitAmount ?? '', Validators.required));
		}

		// Add conditional controls if values already exist
		if (item?.premiumType === 'Percentage') {
			group.addControl('premiumPercentageProperty', this.fb.control(item?.premiumPercentageProperty ?? '', Validators.required));
		}
		if (item?.benefitType === 'Percentage') {
			group.addControl('benefitPercentageProperty', this.fb.control(item?.benefitPercentageProperty ?? '', Validators.required));
		}

		// Listen for premiumType changes
		group.get('premiumType')?.valueChanges.subscribe(value => {
			if (value === 'Percentage') {
				group.addControl('premiumPercentageProperty', this.fb.control('', Validators.required));
			} else {
				group.removeControl('premiumPercentageProperty');
			}
		});

		// Listen for benefitType changes
		group.get('benefitType')?.valueChanges.subscribe(value => {
			if (value === 'Percentage') {
				group.addControl('benefitPercentageProperty', this.fb.control('', Validators.required));
			} else {
				group.removeControl('benefitPercentageProperty');
			}
		});
		return group;
	}

	private buildOfferLineGroup(productType: ProductTypeEnum, item?: ProductOfferLineDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			name: [item?.name ?? '', Validators.required],
			description: [item?.description ?? ''],
			compulsory: [item?.compulsory ?? false],
			details: this.fb.array((item?.details ?? []).map((d: any) => this.buildOfferLineDetailGroup(productType, d))),
		});
	}

	private buildOfferBenefitSplitGroup(item?: ProductOfferBenefitSplitDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			appliedTo: [item?.appliedTo ?? '', Validators.required],
			fromAge: [item?.fromAge ?? 0, Validators.required],
			toAge: [item?.toAge ?? 0, Validators.required],
			benefitAmount: [item?.benefitAmount ?? 0, Validators.required],
		});
	}
	private buildOfferBenefitRuleGroup(item?: ProductOfferBenefitRuleDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			from: [item?.from ?? 0, Validators.required],
			to: [item?.to ?? 0, Validators.required],
			amount: [item?.amount ?? 0, Validators.required],
			recipientType: [item?.recipientType],
			recipient: [item?.recipient],
			calculationType: ['Percentage'],
		});
	}

	private buildOfferGroup(productType: ProductTypeEnum, item?: ProductOfferDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			name: [item?.name ?? '', Validators.required],
			description: [item?.description ?? ''],
			rules: this.fb.array((item?.rules ?? []).map((r: any) => this.buildRuleGroup(r))),
			lines: this.fb.array((item?.lines ?? []).map((l: any) => this.buildOfferLineGroup(productType, l))),
			benefitSplits: this.fb.array((item?.benefitSplits ?? []).map((b: any) => this.buildOfferBenefitSplitGroup(b))),
			benefitRules: this.fb.array((item?.benefitRules ?? []).map((b: any) => this.buildOfferBenefitRuleGroup(b))),
		});
	}

	private buildExtendedDetailGroup(item?: ProductExtendedDetailDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			fromDate: [item?.fromDate ? new Date(item.fromDate) : null, Validators.required],
			toDate: [item?.toDate ? new Date(item.toDate) : null, Validators.required],
			fromAge: [item?.fromAge ?? 0, Validators.required],
			toAge: [item?.toAge ?? 0, Validators.required],
			relationToMainMember: [item?.relationToMainMember ?? 'All', Validators.required],
			premiumType: [item?.premiumType ?? 'Fixed', Validators.required],
			premiumAmount: [item?.premiumAmount ?? 0, Validators.required],
			benefitAmount: [item?.benefitAmount ?? 0, Validators.required],
			commissions: this.fb.array((item?.commissions ?? []).map((c: any) => this.buildCommissionGroup(c))),
		});
	}

	private buildExtendedGroup(item?: ProductExtendedDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			name: [item?.name ?? '', Validators.required],
			description: [item?.description ?? ''],
			details: this.fb.array(
				(item?.details ?? []).map((d: any) => this.buildExtendedDetailGroup(d))
			)
		});
	}
	private buildProductBankAccount(item?: ProductBankAccountDto): FormGroup {
		return this.fb.group({
			id: [item?.id ?? ''],
			fromDate: [item?.fromDate ?? '', Validators.required],
			toDate: [item?.toDate ?? ''],
			bank: [item?.bank, Validators.required],
			accountNumber: [item?.accountNumber, Validators.required],
			accountName: [item?.accountName, Validators.required]
		});
	}

	// ─── Rules ───────────────────────────────────────────────────────────────

	addRule(): void {
		const modal = this.modalService.create({
			nzTitle: 'Create Rule',
			nzContent: ProductEditRuleModalComponent,
			nzWidth: '50%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.rulesArray;
	}

	editRule(entry: any): void {
		const index = this.rulesArray.value.findIndex((r: any) => r === entry);
		const formControl = this.rulesArray.at(index) as FormGroup;

		const modal = this.modalService.create({
			nzTitle: 'Edit Rule',
			nzContent: ProductEditRuleModalComponent,
			nzWidth: '50%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.rulesArray;
		modal.componentInstance!.formControl = formControl;
	}
	// ─── Bank Account ───────────────────────────────────────────────────────────────

	addBankAccount(): void {
		const modal = this.modalService.create({
			nzTitle: 'Create Rule',
			nzContent: ProductEditBankAccountModalComponent,
			nzWidth: '50%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.bankAccountArray;
	}

	editBankAccount(entry: any): void {
		const index = this.bankAccountArray.value.findIndex((r: any) => r === entry);
		const formControl = this.bankAccountArray.at(index) as FormGroup;

		const modal = this.modalService.create({
			nzTitle: 'Edit Rule',
			nzContent: ProductEditBankAccountModalComponent,
			nzWidth: '50%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.bankAccountArray;
		modal.componentInstance!.formControl = formControl;
	}
	// ─── Offers ──────────────────────────────────────────────────────────────

	addOffer(): void {
		const modal = this.modalService.create({
			nzTitle: 'Create Offer',
			nzContent: ProductEditOfferModalComponent,
			nzWidth: '80%',
		});
		modal.componentInstance!.formArray = this.offersArray;
		modal.componentInstance!.productType = this.form.get('productType')!.value;
		modal.componentInstance!.allRecipients = this.availablePremiumRecipients;

	}

	editOffer(entry: any): void {
		const index = this.offersArray.value.findIndex((o: any) => o === entry);
		const formControl = this.offersArray.at(index) as FormGroup;

		const modal = this.modalService.create({
			nzTitle: 'Edit Offer',
			nzContent: ProductEditOfferModalComponent,
			nzWidth: '80%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.offersArray;
		modal.componentInstance!.formControl = formControl;
		modal.componentInstance!.productType = this.form.get('productType')!.value;
		modal.componentInstance!.allRecipients = this.availablePremiumRecipients;
	}

	// ─── Extended Cover ───────────────────────────────────────────────────────

	addExtended(): void {
		const modal = this.modalService.create({
			nzTitle: 'Create Extended Cover',
			nzContent: ProductEditExtendedModalComponent,
			nzWidth: '80%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.extendedArray;
	}

	editExtended(entry: any): void {
		const index = this.extendedArray.value.findIndex((e: any) => e === entry);
		const formControl = this.extendedArray.at(index) as FormGroup;

		const modal = this.modalService.create({
			nzTitle: 'Edit Extended Cover',
			nzContent: ProductEditExtendedModalComponent,
			nzWidth: '80%',
			nzFooter: null,
			nzMaskClosable: false
		});
		modal.componentInstance!.formArray = this.extendedArray;
		modal.componentInstance!.formControl = formControl;
	}

	// ─── Save ─────────────────────────────────────────────────────────────────

	save(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const productId = this.form.get('id')?.value;
		if (productId) {
			this.httpService.update(+productId, this.form.value).subscribe(result => {
				this.router.navigate(['/product', result.id, 'view']);
			});
		} else {
			this.httpService.create(this.form.value).subscribe(result => {
				this.router.navigate(['/product', result.id, 'view']);
			});
		}
	}

	// ─── FormArray Accessors ──────────────────────────────────────────────────
	get rulesArray(): FormArray {
		return this.form.get('rules') as FormArray;
	}

	get offersArray(): FormArray {
		return this.form.get('offers') as FormArray;
	}

	get extendedArray(): FormArray {
		return this.form.get('extendeds') as FormArray;
	}

	get bankAccountArray(): FormArray {
		return this.form.get('bankAccounts') as FormArray;
	}

	get rules(): any[] {
		return this.rulesArray?.value ?? [];
	}
	get bankAccounts(): any[] {
		return this.bankAccountArray?.value ?? [];
	}

	get offers(): any[] {
		return this.offersArray?.value ?? [];
	}

	get extended(): any[] {
		return this.extendedArray?.value ?? [];
	}
	get paymentSentCommunication(): FormGroup {
		return this.form.get('paymentSentCommunication') as FormGroup;
	}



	openClaimWorkflow() {
		this.router.navigate(['workflow', this.product?.id ?? '-', this.form.get('productType')!.value, 'edit'], { relativeTo: this.activatedRoute });
	}

	getAllErrors(form: AbstractControl): Record<string, any> | null {
		if (form instanceof FormControl) {
			return form.errors;
		}

		const errors: Record<string, any> = {};

		if (form instanceof FormGroup) {
			Object.keys(form.controls).forEach(key => {
				const childErrors = this.getAllErrors(form.controls[key]);
				if (childErrors) errors[key] = childErrors;
			});
		}

		if (form instanceof FormArray) {
			form.controls.forEach((control, i) => {
				const childErrors = this.getAllErrors(control);
				if (childErrors) errors[i] = childErrors;
			});
		}

		return Object.keys(errors).length ? errors : null;
	}


	paymentSentCustomUpload = (item: NzUploadXHRArgs): Subscription => {
		const formData = new FormData();
		formData.append('file', item.file as any);

		// Create the subscription
		const sub = this.fileService.upload(item.name ?? ('attachment-' + Date.now()), formData).subscribe({
			next: (res) => {
				// success callback
				item.onSuccess!(res, item.file, event);
				console.log("Upload result: " + res.uuid);
				(this.paymentSentCommunication.get('attachments')! as FormArray).push(new FormControl(res));
			},
			error: (err) => {
				item.onError!(err, item.file);
			}
		});

		// return it so nz-upload can cancel if needed
		return sub;
	};
}
